
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import { apiCall } from '@/lib/api/api-helpers'
import type { JobStatus, JobType } from '@genieartinc/schemas'

/**
 * Job Store - Tracks AI generation jobs for the active canvas
 *
 * Responsibilities:
 * - Store active/recent job records
 * - Provide actions for updating job status (from WebSocket events)
 * - Load persisted jobs on canvas mount
 */

export interface JobRecord {
  id: string
  type: JobType
  status: JobStatus
  nodeId?: string
  canvasId: string
  progress?: number
  message?: string
  output?: unknown
  error?: string
  executionTime?: number
  createdAt: string
  updatedAt?: string
}

interface PlaceholderMapping {
  nodeIds: string[]
  parentNodeId: string
}

interface JobStore {
  // State
  jobs: Map<string, JobRecord>
  activeCanvasId: string | null
  placeholderMap: Map<string, PlaceholderMapping>

  // Actions
  setCanvasId: (canvasId: string) => void
  addJob: (job: JobRecord) => void
  updateJobStatus: (jobId: string, status: JobStatus, data?: Partial<JobRecord>) => void
  updateJobProgress: (jobId: string, progress: number, message?: string) => void
  removeJob: (jobId: string) => void
  loadCanvasJobs: (canvasId: string) => Promise<void>
  clearJobs: () => void

  // Placeholder mapping (for async generation)
  setPlaceholders: (jobId: string, parentNodeId: string, nodeIds: string[]) => void
  getPlaceholders: (jobId: string) => PlaceholderMapping | undefined
  removePlaceholders: (jobId: string) => void

  // Selectors
  getActiveJobs: () => JobRecord[]
  getJobsByNode: (nodeId: string) => JobRecord[]
  getJobsByStatus: (status: JobStatus) => JobRecord[]
}

export const useJobStore = create<JobStore>()(
  subscribeWithSelector((set, get) => ({
    jobs: new Map(),
    activeCanvasId: null,
    placeholderMap: new Map(),

    setCanvasId: (canvasId: string) => {
      set({ activeCanvasId: canvasId })
    },

    addJob: (job: JobRecord) => {
      set((state) => {
        const next = new Map(state.jobs)
        next.set(job.id, job)
        return { jobs: next }
      })
    },

    updateJobStatus: (jobId: string, status: JobStatus, data?: Partial<JobRecord>) => {
      set((state) => {
        const existing = state.jobs.get(jobId)
        if (!existing) return state

        const next = new Map(state.jobs)
        next.set(jobId, {
          ...existing,
          ...data,
          status,
          updatedAt: new Date().toISOString(),
        })
        return { jobs: next }
      })
    },

    updateJobProgress: (jobId: string, progress: number, message?: string) => {
      set((state) => {
        const existing = state.jobs.get(jobId)
        if (!existing) return state

        const next = new Map(state.jobs)
        next.set(jobId, {
          ...existing,
          progress,
          ...(message !== undefined ? { message } : {}),
          updatedAt: new Date().toISOString(),
        })
        return { jobs: next }
      })
    },

    removeJob: (jobId: string) => {
      set((state) => {
        const next = new Map(state.jobs)
        next.delete(jobId)
        return { jobs: next }
      })
    },

    loadCanvasJobs: async (canvasId: string) => {
      try {
        const result = await apiCall(
          `/api/jobs/canvas/${canvasId}?status=PENDING,RUNNING`,
          true
        ) as { success: boolean; data: { jobs: Array<Record<string, unknown>> } }

        if (result.success && result.data?.jobs) {
          set((state) => {
            const next = new Map(state.jobs)
            for (const raw of result.data.jobs) {
              const job: JobRecord = {
                id: raw.id as string,
                type: raw.type as JobType,
                status: raw.status as JobStatus,
                nodeId: raw.nodeId as string | undefined,
                canvasId: raw.canvasId as string,
                progress: raw.progress as number | undefined,
                message: raw.message as string | undefined,
                output: raw.output,
                error: raw.error as string | undefined,
                executionTime: raw.executionTime as number | undefined,
                createdAt: (raw.createdAt as string) || new Date().toISOString(),
                updatedAt: raw.updatedAt as string | undefined,
              }
              next.set(job.id, job)
            }
            return { jobs: next }
          })
        }
      } catch (err) {
        // Jobs endpoint may not exist yet during development - silent fail
        console.warn('Failed to load canvas jobs:', err)
      }
    },

    clearJobs: () => {
      set({ jobs: new Map(), placeholderMap: new Map() })
    },

    setPlaceholders: (jobId: string, parentNodeId: string, nodeIds: string[]) => {
      set((state) => {
        const next = new Map(state.placeholderMap)
        next.set(jobId, { nodeIds, parentNodeId })
        return { placeholderMap: next }
      })
    },

    getPlaceholders: (jobId: string) => {
      return get().placeholderMap.get(jobId)
    },

    removePlaceholders: (jobId: string) => {
      set((state) => {
        const next = new Map(state.placeholderMap)
        next.delete(jobId)
        return { placeholderMap: next }
      })
    },

    getActiveJobs: () => {
      const { jobs } = get()
      return Array.from(jobs.values()).filter(
        (j) => j.status === 'PENDING' || j.status === 'RUNNING'
      )
    },

    getJobsByNode: (nodeId: string) => {
      const { jobs } = get()
      return Array.from(jobs.values()).filter((j) => j.nodeId === nodeId)
    },

    getJobsByStatus: (status: JobStatus) => {
      const { jobs } = get()
      return Array.from(jobs.values()).filter((j) => j.status === status)
    },
  }))
)

export const getJobStore = () => useJobStore.getState()
